import { compact } from "lodash";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { useMemo } from "react";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import { TicketBasket } from "@shared/modules/basket/types";
import { X_SP_UUID } from "@shared/env";
import { usePaymentManager } from "@shared/modules/payments/contexts/PaymentManagerContext";
import { getPostPurchaseMessages } from "@app-contexts/public/shared/checkout/components/confirmation/PostPurchaseMessages/utils/utils";
import usePublicProductsService from "@app-contexts/public/features/products/data/services/usePublicProductsService";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { enums } from "@shared/constants";

export default function useCheckoutConfirmation() {
  const { isLoggedIn } = useUserSessionAuthState();
  const { purchaseData } = usePaymentManager();

  const eventsIds: number[] = useMemo(() => {
    if (!purchaseData?.invoice || !("invoice_items" in purchaseData?.invoice)) {
      return [];
    }
    return compact(
      purchaseData?.invoice.invoice_items?.map((item) => item.event?.id)
    );
  }, [purchaseData]);

  const membershipGroupsIds = useMemo(() => {
    if (!purchaseData?.invoice || !("invoice_items" in purchaseData?.invoice)) {
      return [];
    }
    return compact(
      purchaseData.invoice.invoice_items?.map((item) => item.membership_group)
    );
  }, [purchaseData]);

  const productsIds = useMemo(() => {
    if (!purchaseData?.invoice || !("invoice_items" in purchaseData?.invoice)) {
      return [];
    }
    return compact(
      purchaseData.invoice.invoice_items?.map((item) => item.product?.id)
    );
  }, [purchaseData]);

  const venueId = useMemo(() => {
    if (!purchaseData?.invoice || !("invoice_items" in purchaseData?.invoice)) {
      return undefined;
    }
    return purchaseData.invoice.venue.id;
  }, [purchaseData]);

  const { useRetrieve: useRetrieveVenue } = usePublicVenueService();
  const { data: venue, isLoading: isVenueLoading } = useRetrieveVenue(venueId, {
    options: {
      enabled: Boolean(venueId),
    },
  });

  const { usePaginated: usePaginatedEvents } = usePublicEventService();
  const { data: eventsData, isLoading: isEventsLoading } = usePaginatedEvents({
    params: {
      id__in: eventsIds.join(","),
    },
    options: {
      enabled: Boolean(eventsIds.length),
    },
  });

  const events = useMemo(() => {
    if (eventsData) {
      return eventsData.pages.map((page) => page.results).flat();
    }
    return [];
  }, [eventsData]);

  const { usePaginated: usePaginatedMemberships } =
    usePublicMembershipGroupsService();
  const { data: membershipGroupsData, isLoading: isMembershipGroupsLoading } =
    usePaginatedMemberships({
      params: {
        id__in: membershipGroupsIds.toString(),
      },
      options: {
        enabled: Boolean(membershipGroupsIds.length),
      },
    });

  const membershipGroups = useMemo(() => {
    if (membershipGroupsData) {
      return getFlattenedData(membershipGroupsData);
    }
    return [];
  }, [membershipGroupsData]);

  const { usePaginated: usePaginatedProducts } = usePublicProductsService();
  const { data: productsData, isLoading: isProductsLoading } =
    usePaginatedProducts({
      params: {
        id__in: productsIds.toString(),
      },
      options: {
        enabled: Boolean(productsIds.length),
      },
    });

  const products = useMemo(() => {
    if (productsData) {
      return getFlattenedData(productsData);
    }
    return [];
  }, [productsData]);

  const hasNfcEvent: boolean = useMemo(() => {
    if (!purchaseData?.invoice || !("invoice_items" in purchaseData?.invoice))
      return false;
    return (
      purchaseData.invoice.invoice_items?.some(
        (item) => item.event?.is_nfc_pass_only
      ) ?? false
    );
  }, [purchaseData]);

  const hasVirtualEvent: boolean = useMemo(() => {
    if (!purchaseData?.invoice || !("invoice_items" in purchaseData?.invoice))
      return false;
    return (
      purchaseData.invoice.invoice_items?.some(
        (item) => item.event?.virtual_event_type
      ) ?? false
    );
  }, [purchaseData]);

  const isConfirmationEmailEnabled: boolean = useMemo(
    () => Boolean(venue?.confirmation_email_is_enabled),
    [venue?.confirmation_email_is_enabled]
  );

  const postPurchaseMessages = useMemo(() => {
    const messages = [];

    if (venue?.name && venue?.purchase_extra_text) {
      messages.push({
        name: venue?.name,
        message: venue?.purchase_extra_text,
      });
    }

    if (events.length) {
      messages.push(
        ...getPostPurchaseMessages<PublicBaseEventSerializer>(events)
      );
    }

    if (membershipGroups.length) {
      messages.push(
        ...getPostPurchaseMessages<PublicMembershipGroupSerializer>(
          membershipGroups
        )
      );
    }

    if (products.length) {
      messages.push(
        ...getPostPurchaseMessages<ProductTypeSerializer>(products)
      );
    }

    return messages;
  }, [venue, events.length, membershipGroups.length, products.length]);

  if (!purchaseData?.invoice || !("invoice_items" in purchaseData?.invoice)) {
    return {
      isLoggedIn: Boolean(isLoggedIn),
      basket: purchaseData?.finalBasket,
      invoice: undefined,
      hasNfcEvent: false,
      hasVirtualEvent: false,
      isConfirmationEmailEnabled: false,
      postPurchaseMessages: [],
      venue: undefined,
      isLoading:
        isVenueLoading ||
        isEventsLoading ||
        isMembershipGroupsLoading ||
        isProductsLoading,
    };
  }

  const basket: (TicketBasket & { [X_SP_UUID]?: string }) | undefined =
    purchaseData?.finalBasket;
  const invoice = purchaseData?.invoice;

  /**
   * Checks if the invoice has protection.
   * We determine if the invoice has protection by checking if it has an protection invoice item.
   *
   * We deviate from using `hasActiveInvoiceProtection` because ticket items might not have the protection object
   * at this stage, and we dont care about the status. The user just purchased it, so we just check if it has a protection invoice item.
   * @returns true if the invoice has a protection invoice item, false otherwise.
   */
  const hasProtection = invoice
    ? invoice.invoice_items?.some(
        (item) => item.type === enums.ITEM_TYPES_MAP.ITEM_TYPE_PROTECTION.id
      )
    : false;

  return {
    isLoggedIn: Boolean(isLoggedIn),
    basket,
    invoice,
    hasProtection,
    hasNfcEvent,
    hasVirtualEvent,
    isConfirmationEmailEnabled,
    postPurchaseMessages,
    venue,
    isLoading:
      isVenueLoading ||
      isEventsLoading ||
      isMembershipGroupsLoading ||
      isProductsLoading,
  };
}
